// =====================================================================
// LA PANTALLA DE INGRESO
//
// Primera pantalla migrada a React, y elegida a propósito: es la más
// chica y la que menos depende de lo demás, así que si el andamio
// estuviera mal montado se nota acá y no en la número siete.
//
// DOS COSAS QUE NO HACE, y las dos a propósito:
//
//   · NO DICE SI EL EMAIL EXISTE. El servidor contesta lo mismo en los
//     cuatro casos malos, y esta pantalla muestra ese mensaje tal cual
//     sin «ayudar» con un «esa cuenta no existe». La lista de quiénes
//     compraron lotes acá es información del negocio, y un formulario
//     que la confirma de a un email por vez la regala.
//
//   · NO GUARDA EL TESTIGO. El servidor lo manda en una cookie
//     httpOnly que este JavaScript no puede leer. Por eso no hay nada
//     que guardar en localStorage, y por eso un script inyectado en
//     esta página no se puede llevar la sesión.
// =====================================================================

import { useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';

// A dónde ir después de entrar. Se acota a rutas de este mismo sitio:
// tomar una URL entera del parámetro dejaría armar un link que entra acá
// y rebota a otro lado, que es la forma clásica de robar una sesión.
//
// Y DEPENDE DEL ROL, que es lo que evita un rebote infinito. Un cliente
// que caía en la pantalla de administración era mandado acá con
// «volver=/index.html», entraba bien, volvía a index.html, recibía otro
// 403 y volvía a empezar. Su casa es otra, así que salvo que el link
// pedido sea del portal, va al portal.
//
// DE DÓNDE SALE EL PREFIJO. Desde que el sitio público vive en `/`, las
// pantallas cuelgan de `/sistema/`. Ese prefijo NO está escrito acá: se
// deduce de dónde está esta misma página, así que el día que el sistema
// se mueva a otra dirección —o vuelva a la raíz— esto sigue andando sin
// que nadie se acuerde de venir a tocarlo. Una constante escrita a mano
// habría sido un lugar más donde equivocarse en silencio.
const BASE = location.pathname.replace(/[^/]*$/, '');

function destino(rol) {
  const pedido = new URLSearchParams(location.search).get('volver') || '';
  const limpio = /^\/[A-Za-z0-9._\-\/]*$/.test(pedido) && pedido.startsWith(BASE)
    ? pedido : '';
  if (rol === 'cliente') {
    return limpio.startsWith(BASE + 'cliente') ? limpio : BASE + 'cliente.html';
  }
  // EL VENDEDOR TIENE UNA SOLA PANTALLA (v53), igual que el cliente, y
  // no vuelve a ninguna de administración aunque se lo pidan: todas le
  // contestarían 403.
  if (rol === 'vendedor') {
    return limpio.startsWith(BASE + 'vendedor') ? limpio : BASE + 'vendedor.html';
  }
  // LA CASA DE SISTEMA ES LA AUDITORÍA (v131). Javi, el 17/10: «para
  // sistema el menú de inicio es auditoría».
  //
  // Es el mismo razonamiento que puso el tablero de casa del
  // administrador, aplicado a otro trabajo. Lo que el administrador no
  // puede olvidarse de mirar es la cola: pagos esperando, preventas,
  // recibos. Lo que Sistema no puede olvidarse de mirar es si las
  // cuentas cierran — y eso no lo reclama nadie, porque una
  // inconsistencia no hace cola ni llama por teléfono.
  //
  // El tablero no se pierde: `inicio.html` lleva `deAdministracion`, así
  // que a Sistema le sigue quedando dentro de Tareas Administrativas.
  if (rol === 'sistema') {
    return limpio && !limpio.startsWith(BASE + 'cliente') ? limpio : BASE + 'auditoria.html';
  }
  // La casa de quien administra es el tablero, no el estado de cuenta.
  // Es un cambio chico con un motivo grande: entrar directo a una
  // pantalla de consulta obliga a acordarse de ir a mirar la cola, y lo
  // que no se recuerda no se hace. El tablero muestra lo que espera
  // antes de que nadie lo busque.
  return limpio && !limpio.startsWith(BASE + 'cliente') ? limpio : BASE + 'inicio.html';
}

function Aviso({ clase, texto }) {
  if (!texto) return null;
  return <p className={`aviso ${clase}`}>{texto}</p>;
}

// El encabezado de la columna derecha: el isotipo, el sobretítulo en
// versales ocre y el título. Es el mismo en los dos modos —ingresar y
// recuperar— y cambia solo el texto, así que vive en un lugar.
function Encabezado({ sobretitulo, titulo, bajada }) {
  return (
    <header className="acceso-cabeza">
      <img src="img/isotipo-color.svg" alt="" width="64" height="64" />
      <p className="sobretitulo">{sobretitulo}</p>
      <h1>{titulo}</h1>
      <p className="bajada">{bajada}</p>
    </header>
  );
}

// El pie de la columna: la salida para quien todavía no es cliente. Va
// separado por una línea y no por un botón, porque no es una acción del
// sistema: es volver al sitio.
function NoSoyCliente() {
  return (
    <footer className="acceso-pie">
      <p>¿Todavía no sos cliente?</p>
      <a href="/#contacto">Volver al sitio y dejar una consulta</a>
    </footer>
  );
}

function Entrar({ email, setEmail, alOlvidar }) {
  const [contrasena, setContrasena] = useState('');
  const [aviso, setAviso] = useState(null);
  const [yendo, setYendo] = useState(false);
  const campoContrasena = useRef(null);

  async function enviar(ev) {
    ev.preventDefault();
    setAviso(null);
    setYendo(true);
    try {
      const r = await fetch('/api/sesion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, contrasena }),
      });
      const cuerpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(cuerpo.error || 'No se pudo entrar.');
      location.href = destino(cuerpo.usuario && cuerpo.usuario.rol);
    } catch (e) {
      setAviso({ clase: 'mal', texto: e.message });
      setContrasena('');
      if (campoContrasena.current) campoContrasena.current.focus();
    } finally {
      setYendo(false);
    }
  }

  return (
    <form id="forma" onSubmit={enviar}>
      {/* EL TÍTULO ES LA FRASE FIJA, y el botón dice «Ingresar» a secas.
          El manual fija «Ingresar al Sistema» como las palabras del
          acceso, y la referencia del propio diseñador las pone acá
          arriba: repetirlas también en el botón las diría dos veces en
          la misma pantalla. */}
      <Encabezado sobretitulo="Acceso al sistema" titulo="Ingresar al Sistema"
                  bajada="Usá el email con el que te dimos de alta." />

      <div id="aviso"><Aviso {...(aviso || {})} /></div>

      <div className="campo">
        <label htmlFor="email">Email</label>
        {/* autocomplete con los nombres que el gestor de contraseñas del
            navegador entiende: sin esto guarda mal el par y la gente
            termina escribiéndolo a mano cada vez. */}
        <input type="email" id="email" name="email" autoComplete="username"
               required autoFocus value={email}
               onChange={(e) => setEmail(e.target.value)} />
      </div>

      <div className="campo">
        <label htmlFor="contrasena">Contraseña</label>
        <input type="password" id="contrasena" name="contrasena"
               autoComplete="current-password" required ref={campoContrasena}
               value={contrasena} onChange={(e) => setContrasena(e.target.value)} />
      </div>

      <button type="submit" className="accion" id="boton" disabled={yendo}>
        {yendo ? 'Ingresando…' : 'Ingresar'}
      </button>

      <p className="pie">
        <button type="button" className="enlace" id="olvide" onClick={alOlvidar}>
          Olvidé mi contraseña
        </button>
      </p>

      <NoSoyCliente />
    </form>
  );
}

// MODO RECUPERAR, en la misma caja.
//
// La versión anterior no tenía esto: tocar «olvidé mi contraseña» con el
// email vacío contestaba «escribí tu email arriba y volvé a tocar acá», y
// ese «acá» era el propio enlace, tres renglones más abajo del lugar
// donde decía «arriba». Javi lo encontró en el primer intento. Ahora no
// hay nada que interpretar: el formulario cambia, y lo que hay que
// llenar es lo único que se ve.
function Recuperar({ email, setEmail, alVolver }) {
  const [aviso, setAviso] = useState(null);
  const [yendo, setYendo] = useState(false);

  async function enviar(ev) {
    ev.preventDefault();
    setAviso(null);
    setYendo(true);
    try {
      const r = await fetch('/api/acceso/recuperar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const cuerpo = await r.json().catch(() => ({}));
      // EL FRENO POR IP CONTESTA 429, y eso NO es un éxito (v64). Sin
      // esto la pantalla mostraba el mensaje de siempre en verde: le
      // decía a la persona que el link salió cuando no había salido.
      if (!r.ok) {
        setAviso({ clase: 'mal', texto: cuerpo.error ||
          'No se pudo pedir el link. Probá de nuevo en un momento.' });
        return;
      }
      // Contesta siempre lo mismo, exista la cuenta o no: es la misma
      // regla de no delatar qué emails están dados de alta.
      setAviso({ clase: 'bien', texto: cuerpo.mensaje ||
        'Si esa dirección corresponde a una cuenta, te llega un correo con el link.' });
    } catch {
      setAviso({ clase: 'mal',
        texto: 'No se pudo pedir el link. Probá de nuevo en un momento.' });
    } finally {
      setYendo(false);
    }
  }

  return (
    <form id="forma-recuperar" onSubmit={enviar}>
      <Encabezado sobretitulo="Acceso al sistema" titulo="Recuperar el Acceso"
                  bajada="Te mandamos un link para elegir una contraseña nueva." />

      <div id="aviso-recuperar"><Aviso {...(aviso || {})} /></div>

      <div className="campo">
        <label htmlFor="email-recuperar">Email</label>
        <input type="email" id="email-recuperar" autoComplete="username" required
               autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>

      <button type="submit" className="accion" id="boton-recuperar" disabled={yendo}>
        {yendo ? 'Enviando…' : 'Enviarme el link'}
      </button>

      <p className="pie">
        <button type="button" className="enlace" id="volver" onClick={alVolver}>
          Volver al ingreso
        </button>
      </p>

      <NoSoyCliente />
    </form>
  );
}

function Pantalla() {
  const [modo, setModo] = useState('ingreso');
  // El email vive acá arriba, compartido por los dos formularios: quien
  // se dio cuenta de que olvidó la contraseña DESPUÉS de escribirlo no
  // tiene que volver a escribirlo. Antes eran dos campos y una copia a
  // mano de uno al otro.
  const [email, setEmail] = useState('');

  return modo === 'ingreso'
    ? <Entrar email={email} setEmail={setEmail} alOlvidar={() => setModo('recuperar')} />
    : <Recuperar email={email} setEmail={setEmail} alVolver={() => setModo('ingreso')} />;
}

createRoot(document.getElementById('raiz')).render(<Pantalla />);
